import { createId } from "@paralleldrive/cuid2";
import {
	createFileRoute,
	Navigate,
	notFound,
	useLoaderData,
} from "@tanstack/react-router";
import { commitMutation, fetchQuery, graphql } from "react-relay";
import { environment } from "@/relay/environment";
import { PaymentProviderInfo } from "@/utils/payment-provider-info";
import type { integracoesCheckoutNovoExistingQuery } from "./__generated__/integracoesCheckoutNovoExistingQuery.graphql";
import type {
	ExternalPaymentProvider,
	integracoesCheckoutNovoMutation,
	integracoesCheckoutNovoMutation$data,
} from "./__generated__/integracoesCheckoutNovoMutation.graphql";

const createIntegration = async ({
	slug,
	key,
}: {
	slug: string;
	key: ExternalPaymentProvider;
}): Promise<
	integracoesCheckoutNovoMutation$data["paymentIntegrationCreate"]
> => {
	return new Promise((resolve, reject) => {
		commitMutation<integracoesCheckoutNovoMutation>(environment, {
			mutation: graphql`
    		mutation integracoesCheckoutNovoMutation($club: String!, $input: PaymentIntegrationCreateInput!) {
    			paymentIntegrationCreate(club: $club, paymentIntegrationCreateInput: $input) {
    			  id
    			}
    		}
      `,
			variables: {
				club: slug,
				input: {
					provider: key,
					hash: createId(),
					products: [],
				},
			},
			onCompleted: (res, errors) => {
				if (errors?.length) {
					reject(new Error(errors[0].message));
					return;
				}
				resolve(res.paymentIntegrationCreate);
			},
			onError: reject,
		});
	});
};

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/configuracoes/integracoes/checkout/$slug/novo",
)({
	component: RouteComponent,
	loader: async ({ context, params }) => {
		const providerInfo = Object.values(PaymentProviderInfo).find(
			(info) => info.slug === params.slug,
		);

		if (!providerInfo) {
			throw notFound();
		}
		const current = await fetchQuery<integracoesCheckoutNovoExistingQuery>(
			environment,
			graphql`
				query integracoesCheckoutNovoExistingQuery($club: String!) {
					club(slug: $club) {
						integrations {
							__typename
							id
							... on PaymentIntegration {
								provider
							}
						}
					}
				}
			`,
			{ club: context.club.slug },
		).toPromise();
		const existing = current?.club.integrations.find(
			(integration) =>
				integration.__typename === "PaymentIntegration" &&
				integration.provider === providerInfo.key,
		);
		if (existing) {
			return { integrationId: existing.id };
		}

		const integration = await createIntegration({
			slug: context.club.slug,
			key: providerInfo.key,
		})
			.then((res) => res)
			.catch(() => null);

		if (!integration) {
			throw new Error("Failed to create integration");
		}

		return { integrationId: integration.id };
	},
});

function RouteComponent() {
	const { integrationId } = useLoaderData({ from: Route.id });

	return (
		<Navigate
			from={Route.fullPath}
			to="/c/$club/configuracoes/integracoes/checkout/$id"
			params={{ id: integrationId }}
		/>
	);
}
